@if (target()) {
  <div class="fixed inset-0 z-[60] flex items-center justify-center bg-ink-primary/40 px-4" (click)="close()">
    <div
      class="flex max-h-[88vh] w-full max-w-4xl flex-col rounded-lg bg-surface shadow-flyout"
      role="dialog"
      aria-modal="true"
      (click)="$event.stopPropagation()"
    >
      @if (order(); as o) {
        <!-- Header -->
        <div class="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
          <div class="min-w-0">
            <p class="text-label uppercase text-ink-muted">{{ o.title }}</p>
            <div class="mt-0.5 flex flex-wrap items-center gap-2">
              <h2 class="text-section-title" [class]="o.kind === 'purchase-order' ? 'text-brand' : 'text-action'">{{ o.number }}</h2>
              <pp-status-badge [status]="o.status"></pp-status-badge>
              <pp-status-badge [status]="o.paymentStatus"></pp-status-badge>
            </div>
            <p class="mt-1 text-[13px] text-ink-secondary">
              {{ o.partyLabel }}:
              <a [routerLink]="o.partyLink" (click)="close()" class="font-medium text-action hover:underline">{{ o.partyName }}</a>
            </p>
          </div>
          <button type="button" (click)="close()" class="text-ink-muted hover:text-ink-secondary transition-colors" aria-label="Close">
            <pp-icon name="x" [size]="18"></pp-icon>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto px-5 py-4">
          @if (o.flagged) {
            <div class="mb-4 rounded-md border border-status-warning/30 bg-status-warning/5 px-3 py-2 text-[13px] text-status-warning">
              <span class="font-semibold">Flagged · {{ o.flagReason }}</span>
              @if (o.flagNote) {
                <span class="text-ink-secondary"> — {{ o.flagNote }}</span>
              }
            </div>
          }

          <!-- Fields -->
          <div class="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
            @for (field of o.fields; track field.label) {
              <div>
                <p class="text-label uppercase text-ink-muted">{{ field.label }}</p>
                <p class="mt-0.5 text-[13px] text-ink-primary break-words">
                  @if (field.value) {
                    {{ isDate(field) ? (field.value | date: 'MMM d, y') : field.value }}
                  } @else {
                    —
                  }
                </p>
              </div>
            }
          </div>

          <!-- Items -->
          <div class="mt-5 overflow-x-auto rounded-md border border-border-subtle">
            <table class="w-full border-collapse text-left">
              <thead class="bg-surface-canvas">
                <tr class="border-b border-border-subtle">
                  <th class="px-3 py-2 text-label uppercase text-ink-muted font-medium">Item</th>
                  <th class="px-3 py-2 text-label uppercase text-ink-muted font-medium">Lots</th>
                  <th class="px-3 py-2 text-label uppercase text-ink-muted font-medium text-right">Quantity</th>
                  @if (o.kind === 'purchase-order') {
                    <th class="px-3 py-2 text-label uppercase text-ink-muted font-medium text-right">Received</th>
                  }
                  <th class="px-3 py-2 text-label uppercase text-ink-muted font-medium text-right">Price</th>
                  <th class="px-3 py-2 text-label uppercase text-ink-muted font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                @for (line of o.lines; track line.key) {
                  <tr class="border-b border-border-subtle last:border-0 align-top">
                    <td class="px-3 py-2 text-[13px] text-ink-primary">
                      {{ line.product }}
                      @if (!line.isInventoryItem) {
                        <span class="ml-1 text-[11px] text-ink-muted">({{ line.category || 'non-inventory' }})</span>
                      }
                    </td>
                    <td class="px-3 py-2 text-[12px] text-ink-secondary">
                      @for (lot of line.lots; track $index) {
                        <div>
                          @if (lot.purchaseOrderId && o.kind === 'sales-order') {
                            <a [routerLink]="['/orders/purchase-orders', lot.purchaseOrderId]" class="text-brand hover:underline">{{ lot.label }}</a>
                          } @else {
                            {{ lot.label }}
                          }
                        </div>
                      } @empty {
                        —
                      }
                    </td>
                    <td class="px-3 py-2 text-right text-[13px] tabular-nums text-ink-secondary whitespace-nowrap">
                      {{ line.quantity | number: '1.0-2' }} {{ line.unit }}
                    </td>
                    @if (o.kind === 'purchase-order') {
                      <td class="px-3 py-2 text-right text-[13px] tabular-nums text-ink-secondary">
                        {{ line.received !== undefined ? (line.received | number: '1.0-2') : '—' }}
                      </td>
                    }
                    <td class="px-3 py-2 text-right text-[13px] tabular-nums text-ink-secondary whitespace-nowrap">
                      {{ line.unitPrice | ppMoney: 'USD' : 'symbol' : '1.2-4' }}
                    </td>
                    <td class="px-3 py-2 text-right text-[13px] font-medium tabular-nums text-ink-primary whitespace-nowrap">
                      {{ line.total | ppMoney: 'USD' : 'symbol' : '1.2-2' }}
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="6" class="px-3 py-4 text-[13px] text-ink-muted">No items.</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <!-- Totals -->
          <div class="mt-3 flex justify-end">
            <dl class="grid grid-cols-[auto_auto] gap-x-6 gap-y-1 text-[13px]">
              <dt class="text-ink-secondary">Expenses</dt>
              <dd class="text-right tabular-nums text-ink-primary">{{ o.expensesTotal | ppMoney: 'USD' : 'symbol' : '1.2-2' }}</dd>
              <dt class="font-semibold text-ink-primary">Total</dt>
              <dd class="text-right font-semibold tabular-nums text-ink-primary">{{ o.total | ppMoney: 'USD' : 'symbol' : '1.2-2' }}</dd>
            </dl>
          </div>

          @if (o.notes) {
            <div class="mt-4">
              <p class="text-label uppercase text-ink-muted">Notes</p>
              <p class="mt-1 whitespace-pre-line text-[13px] text-ink-secondary">{{ o.notes }}</p>
            </div>
          }

          <div class="mt-5 border-t border-border-subtle pt-4">
            <pp-attachments [owner]="o.kind === 'purchase-order' ? 'purchase-orders' : 'sales-orders'" [ownerId]="o.id" [embedded]="true" [canEdit]="false"></pp-attachments>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-2 border-t border-border-subtle px-5 py-3">
          <button
            type="button"
            (click)="close()"
            class="rounded-md border border-border px-3.5 py-1.5 text-[13px] font-medium text-ink-secondary hover:bg-surface-canvas transition-colors"
          >
            Close
          </button>
          <button
            *ngIf="access.can(o.kind === 'purchase-order' ? 'purchaseOrders.view' : 'salesOrders.view')"
            type="button"
            (click)="openFullOrder(o)"
            class="flex items-center gap-1.5 rounded-md bg-brand px-3.5 py-1.5 text-[13px] font-medium text-white hover:bg-brand-dark transition-colors"
          >
            <pp-icon name="external-link" [size]="14"></pp-icon>
            Open full order
          </button>
        </div>
      } @else {
        <div class="flex items-center justify-between px-5 py-4">
          <p class="text-[13px] text-ink-secondary">
            {{ loading() ? 'Loading order…' : 'This order no longer exists or you do not have access to it.' }}
          </p>
          <button type="button" (click)="close()" class="text-ink-muted hover:text-ink-secondary" aria-label="Close">
            <pp-icon name="x" [size]="18"></pp-icon>
          </button>
        </div>
      }
    </div>
  </div>
}
